import type { Dispatch, ReactNode, SetStateAction } from "react";
import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import {
  ChevronLeft,
  ChevronRight,
  Filter,
  Heart,
  Home,
  MapPin,
  Minus,
  Plus,
  Search,
  SlidersHorizontal,
  Star,
  Users,
  X,
} from "lucide-react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { hotelApi } from "@/api/endpoints";
import { errorMessage } from "@/api/client";
import { useAuth } from "@/store/auth";
import { useRequireAuth } from "@/hooks/useRequireAuth";
import { parseIntent } from "@/lib/authIntent";
import { useWishlist } from "@/hooks/useWishlist";
import { asArray } from "@/lib/asArray";
import type { Hotel } from "@/types";

const HEADER_IMG =
  "https://images.unsplash.com/photo-1566073771259-6a8506099945?auto=format&fit=crop&w=2000&q=80";

const AMENITY_OPTIONS = ["Pool", "Spa", "WiFi", "Restaurant", "Bar", "Gym", "Parking", "Room Service"];
const TYPE_OPTIONS = ["Heritage", "Boutique", "Resort", "Homestay"];
const RATING_OPTIONS = [
  { label: "Any", value: 0 },
  { label: "3.5+", value: 3.5 },
  { label: "4+", value: 4 },
  { label: "4.5+", value: 4.5 },
];
const PAGE_SIZE = 6;

/* -------------------------------- Page ---------------------------------- */
export default function Hotels() {
  const { status } = useAuth();
  const wishlist = useWishlist();
  const requireAuth = useRequireAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [resumeNotice, setResumeNotice] = useState<string | null>(null);
  const resumedRef = useRef(false);

  const [hotels, setHotels] = useState<Hotel[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Search bar
  const [destination, setDestination] = useState("");
  const [checkIn, setCheckIn] = useState("");
  const [checkOut, setCheckOut] = useState("");
  const [guests, setGuests] = useState(2);

  // Filters
  const [priceMax, setPriceMax] = useState(10000);
  const [priceCeil, setPriceCeil] = useState(10000);
  const [minRating, setMinRating] = useState(0);
  const [amenities, setAmenities] = useState<Set<string>>(new Set());
  const [types, setTypes] = useState<Set<string>>(new Set());
  const [sort, setSort] = useState<"recommended" | "price-asc" | "price-desc" | "rating">("recommended");

  const [visible, setVisible] = useState(PAGE_SIZE);
  const [showFilters, setShowFilters] = useState(false); // mobile drawer

  useEffect(() => {
    hotelApi
      .list()
      .then((r) => {
        const list = asArray<Hotel>(r.data);
        setHotels(list);
        const max = Math.max(1000, ...list.map((h) => Math.ceil(h.price_per_night / 500) * 500));
        setPriceCeil(max);
        setPriceMax(max);
      })
      .catch((e) => {
        setError(errorMessage(e, "Could not load hotels"));
        setHotels([]);
      });
  }, []);

  // Resume a pending action after the user comes back from login.
  useEffect(() => {
    if (resumedRef.current || status !== "authenticated") return;
    const parsed = parseIntent(searchParams.get("intent"));
    if (!parsed) return;
    resumedRef.current = true;
    if (parsed.verb === "wishlist" && parsed.id) {
      if (!wishlist.ids.has(parsed.id)) wishlist.toggle(parsed.id);
      setResumeNotice("Saved to your wishlist.");
    } else if (parsed.verb === "book" && parsed.id) {
      navigate("/dashboard");
      return;
    }
    const next = new URLSearchParams(searchParams);
    next.delete("intent");
    setSearchParams(next, { replace: true });
  }, [status, searchParams, wishlist, navigate, setSearchParams]);

  const toggleIn = (setter: Dispatch<SetStateAction<Set<string>>>, val: string) =>
    setter((prev) => {
      const next = new Set(prev);
      if (next.has(val)) next.delete(val);
      else next.add(val);
      return next;
    });

  const filtered = useMemo(() => {
    if (!hotels) return [];
    let list = hotels.filter((h) => {
      if (destination && !`${h.name} ${h.location}`.toLowerCase().includes(destination.toLowerCase()))
        return false;
      if (h.price_per_night > priceMax) return false;
      if (h.rating < minRating) return false;
      if (types.size && (!h.property_type || !types.has(h.property_type))) return false;
      if (amenities.size && ![...amenities].every((a) => h.amenities.includes(a))) return false;
      return true;
    });
    if (sort === "price-asc") list = [...list].sort((a, b) => a.price_per_night - b.price_per_night);
    else if (sort === "price-desc") list = [...list].sort((a, b) => b.price_per_night - a.price_per_night);
    else if (sort === "rating") list = [...list].sort((a, b) => b.rating - a.rating);
    return list;
  }, [hotels, destination, priceMax, minRating, types, amenities, sort]);

  // Reset pagination whenever the result set changes.
  useEffect(() => setVisible(PAGE_SIZE), [destination, priceMax, minRating, types, amenities, sort]);

  const clearAll = () => {
    setDestination("");
    setMinRating(0);
    setAmenities(new Set());
    setTypes(new Set());
    setPriceMax(priceCeil);
  };

  const activeFilters = amenities.size + types.size + (minRating > 0 ? 1 : 0) + (priceMax < priceCeil ? 1 : 0);

  return (
    <div className="min-h-screen bg-cream">
      <Navbar overlay />

      <main>
        {/* Slim hero */}
        <section className="relative min-h-[300px] w-full overflow-hidden">
          <img src={HEADER_IMG} alt="Heritage hotel in Rajasthan" className="absolute inset-0 h-full w-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-b from-navy-900/85 via-navy-900/70 to-navy-900/90" />
          <div className="container-page relative pt-28 pb-10">
            <nav className="mb-3 flex items-center gap-1.5 text-sm text-white/70">
              <Link to="/" className="flex items-center gap-1 transition hover:text-gold-400">
                <Home size={14} /> Home
              </Link>
              <ChevronRight size={14} />
              <span className="text-white">Hotels</span>
            </nav>
            <h1 className="font-display text-4xl font-bold text-white sm:text-5xl">Find your heritage stay</h1>
            <p className="mt-2 max-w-xl text-white/75">
              Handpicked palaces, havelis and boutique hotels across Rajasthan.
            </p>
          </div>
        </section>

        {/* Sticky search bar */}
        <div className="sticky top-16 z-20 border-b border-slate-200 bg-white/95 backdrop-blur">
          <div className="container-page py-3">
            <div className="flex flex-col gap-2 rounded-2xl border border-slate-200 bg-white p-2 shadow-card lg:flex-row lg:items-stretch">
              <SearchField icon={<MapPin size={18} />} label="Destination">
                <input
                  value={destination}
                  onChange={(e) => setDestination(e.target.value)}
                  placeholder="City, hotel or area"
                  className="w-full bg-transparent text-sm text-navy-900 placeholder:text-slate-400 focus:outline-none"
                />
              </SearchField>
              <Divider />
              <SearchField icon={<span className="text-xs font-semibold text-slate-400">IN</span>} label="Check-in">
                <input type="date" value={checkIn} onChange={(e) => setCheckIn(e.target.value)} className="w-full bg-transparent text-sm text-navy-900 focus:outline-none" />
              </SearchField>
              <Divider />
              <SearchField icon={<span className="text-xs font-semibold text-slate-400">OUT</span>} label="Check-out">
                <input type="date" value={checkOut} onChange={(e) => setCheckOut(e.target.value)} className="w-full bg-transparent text-sm text-navy-900 focus:outline-none" />
              </SearchField>
              <Divider />
              <GuestCounter value={guests} onChange={setGuests} />
              <button className="btn-primary shrink-0 justify-center px-6 py-3 lg:px-8">
                <Search size={18} /> Search
              </button>
            </div>
          </div>
        </div>

        {/* Results with sidebar */}
        <section className="container-page py-8">
          {error && (
            <div className="mb-6 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">{error}</div>
          )}
          {resumeNotice && (
            <div className="mb-6 flex items-center gap-2 rounded-lg border border-green-200 bg-green-50 px-4 py-3 text-sm text-green-700">
              <Heart size={16} className="fill-green-500 text-green-500" /> {resumeNotice}
            </div>
          )}

          <div className="lg:grid lg:grid-cols-[280px_1fr] lg:gap-8">
            {/* Filters — sidebar on desktop, drawer on mobile */}
            <FiltersPanel
              open={showFilters}
              onClose={() => setShowFilters(false)}
              priceMax={priceMax}
              priceCeil={priceCeil}
              setPriceMax={setPriceMax}
              minRating={minRating}
              setMinRating={setMinRating}
              amenities={amenities}
              toggleAmenity={(a) => toggleIn(setAmenities, a)}
              types={types}
              toggleType={(t) => toggleIn(setTypes, t)}
              onClear={clearAll}
              activeFilters={activeFilters}
            />

            {/* Main column */}
            <div>
              <div className="mb-5 flex flex-wrap items-center justify-between gap-3">
                <div className="text-sm text-slate-600">
                  {hotels === null ? "Loading stays…" : (
                    <>
                      <span className="font-semibold text-navy-900">{filtered.length}</span> stay
                      {filtered.length === 1 ? "" : "s"} found
                    </>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setShowFilters(true)}
                    className="flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm font-medium text-navy-800 lg:hidden"
                  >
                    <Filter size={15} /> Filters{activeFilters > 0 ? ` (${activeFilters})` : ""}
                  </button>
                  <div className="flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-2">
                    <SlidersHorizontal size={15} className="text-slate-400" />
                    <select
                      value={sort}
                      onChange={(e) => setSort(e.target.value as typeof sort)}
                      className="bg-transparent text-sm text-navy-800 focus:outline-none"
                    >
                      <option value="recommended">Recommended</option>
                      <option value="price-asc">Price: Low to High</option>
                      <option value="price-desc">Price: High to Low</option>
                      <option value="rating">Top Rated</option>
                    </select>
                  </div>
                </div>
              </div>

              {hotels === null ? (
                <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                  {Array.from({ length: 4 }).map((_, i) => (
                    <div key={i} className="h-80 animate-pulse rounded-2xl bg-white" />
                  ))}
                </div>
              ) : filtered.length === 0 ? (
                <div className="rounded-2xl border border-slate-200 bg-white py-16 text-center">
                  <p className="font-display text-lg font-semibold text-navy-900">No stays match your filters</p>
                  <p className="mt-1 text-sm text-slate-500">Try widening the price range or clearing some filters.</p>
                  <button onClick={clearAll} className="btn-outline mt-4">Clear all filters</button>
                </div>
              ) : (
                <>
                  <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                    {filtered.slice(0, visible).map((h) => (
                      <HotelCard
                        key={h.public_id}
                        hotel={h}
                        saved={wishlist.ids.has(h.public_id)}
                        onSave={() => requireAuth(`wishlist:${h.public_id}`, () => wishlist.toggle(h.public_id))}
                        onBook={() => requireAuth(`book:${h.public_id}`, () => navigate("/dashboard"))}
                      />
                    ))}
                  </div>

                  {visible < filtered.length && (
                    <div className="mt-8 flex justify-center">
                      <button
                        onClick={() => setVisible((v) => v + PAGE_SIZE)}
                        className="btn-outline px-8 py-3"
                      >
                        Load more stays ({filtered.length - visible} left)
                      </button>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}

/* --------------------------- Search bar pieces -------------------------- */
function SearchField({ icon, label, children }: { icon: ReactNode; label: string; children: ReactNode }) {
  return (
    <label className="flex flex-1 items-center gap-3 rounded-xl px-3 py-2 transition hover:bg-cream">
      <span className="grid h-8 w-8 shrink-0 place-items-center rounded-lg bg-cream text-iris-500">{icon}</span>
      <span className="min-w-0 flex-1">
        <span className="block text-[11px] font-medium uppercase tracking-wide text-slate-400">{label}</span>
        {children}
      </span>
    </label>
  );
}

function Divider() {
  return <div className="hidden w-px self-stretch bg-slate-100 lg:block" />;
}

function GuestCounter({ value, onChange }: { value: number; onChange: (n: number) => void }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="relative flex-1">
      {open && <div className="fixed inset-0 z-10" onClick={() => setOpen(false)} />}
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex w-full items-center gap-3 rounded-xl px-3 py-2 text-left transition hover:bg-cream"
      >
        <span className="grid h-8 w-8 shrink-0 place-items-center rounded-lg bg-cream text-iris-500">
          <Users size={18} />
        </span>
        <span>
          <span className="block text-[11px] font-medium uppercase tracking-wide text-slate-400">Guests</span>
          <span className="text-sm text-navy-900">{value} {value === 1 ? "guest" : "guests"}</span>
        </span>
      </button>
      {open && (
        <div className="absolute left-0 top-full z-20 mt-2 w-56 rounded-xl border border-slate-100 bg-white p-4 shadow-lift">
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium text-navy-900">Guests</span>
            <div className="flex items-center gap-3">
              <button
                type="button"
                onClick={() => onChange(Math.max(1, value - 1))}
                className="grid h-8 w-8 place-items-center rounded-full border border-slate-200 text-navy-700 hover:bg-cream disabled:opacity-40"
                disabled={value <= 1}
              >
                <Minus size={14} />
              </button>
              <span className="w-6 text-center text-sm font-semibold text-navy-900">{value}</span>
              <button
                type="button"
                onClick={() => onChange(Math.min(12, value + 1))}
                className="grid h-8 w-8 place-items-center rounded-full border border-slate-200 text-navy-700 hover:bg-cream disabled:opacity-40"
                disabled={value >= 12}
              >
                <Plus size={14} />
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/* ------------------------------- Filters -------------------------------- */
function FiltersPanel({
  open,
  onClose,
  priceMax,
  priceCeil,
  setPriceMax,
  minRating,
  setMinRating,
  amenities,
  toggleAmenity,
  types,
  toggleType,
  onClear,
  activeFilters,
}: {
  open: boolean;
  onClose: () => void;
  priceMax: number;
  priceCeil: number;
  setPriceMax: (n: number) => void;
  minRating: number;
  setMinRating: (n: number) => void;
  amenities: Set<string>;
  toggleAmenity: (a: string) => void;
  types: Set<string>;
  toggleType: (t: string) => void;
  onClear: () => void;
  activeFilters: number;
}) {
  const body = (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="font-display text-lg font-semibold text-navy-900">Filters</h2>
        {activeFilters > 0 && (
          <button onClick={onClear} className="text-xs font-semibold text-iris-600 hover:text-iris-700">
            Clear all
          </button>
        )}
      </div>

      {/* Price */}
      <div>
        <h3 className="mb-2 text-sm font-semibold text-navy-800">Price per night</h3>
        <input
          type="range"
          min={0}
          max={priceCeil}
          step={500}
          value={priceMax}
          onChange={(e) => setPriceMax(Number(e.target.value))}
          className="w-full accent-iris-500"
        />
        <div className="mt-1 flex justify-between text-xs text-slate-500">
          <span>₹0</span>
          <span className="font-semibold text-navy-900">up to ₹{priceMax.toLocaleString("en-IN")}</span>
        </div>
      </div>

      {/* Rating */}
      <div>
        <h3 className="mb-2 text-sm font-semibold text-navy-800">Star rating</h3>
        <div className="flex flex-wrap gap-2">
          {RATING_OPTIONS.map((r) => (
            <button
              key={r.value}
              onClick={() => setMinRating(r.value)}
              className={`flex items-center gap-1 rounded-full border px-3 py-1.5 text-xs font-medium transition ${
                minRating === r.value
                  ? "border-iris-500 bg-iris-50 text-iris-600"
                  : "border-slate-200 text-navy-700 hover:bg-cream"
              }`}
            >
              {r.value > 0 && <Star size={12} className="fill-gold-500 text-gold-500" />}
              {r.label}
            </button>
          ))}
        </div>
      </div>

      {/* Property type */}
      <div>
        <h3 className="mb-2 text-sm font-semibold text-navy-800">Property type</h3>
        <div className="space-y-2">
          {TYPE_OPTIONS.map((t) => (
            <Checkbox key={t} label={t} checked={types.has(t)} onChange={() => toggleType(t)} />
          ))}
        </div>
      </div>

      {/* Amenities */}
      <div>
        <h3 className="mb-2 text-sm font-semibold text-navy-800">Amenities</h3>
        <div className="space-y-2">
          {AMENITY_OPTIONS.map((a) => (
            <Checkbox key={a} label={a} checked={amenities.has(a)} onChange={() => toggleAmenity(a)} />
          ))}
        </div>
      </div>
    </div>
  );

  return (
    <>
      {/* Desktop sidebar */}
      <aside className="hidden lg:block">
        <div className="sticky top-32 rounded-2xl border border-slate-200 bg-white p-5 shadow-card">{body}</div>
      </aside>

      {/* Mobile drawer */}
      {open && (
        <div className="fixed inset-0 z-50 lg:hidden">
          <div className="absolute inset-0 bg-black/40" onClick={onClose} />
          <div className="absolute inset-y-0 left-0 w-80 max-w-[85vw] overflow-y-auto bg-white p-5">
            <button onClick={onClose} className="mb-4 flex items-center gap-1 text-sm text-slate-500">
              <X size={16} /> Close
            </button>
            {body}
            <button onClick={onClose} className="btn-primary mt-6 w-full">Show results</button>
          </div>
        </div>
      )}
    </>
  );
}

function Checkbox({ label, checked, onChange }: { label: string; checked: boolean; onChange: () => void }) {
  return (
    <label className="flex cursor-pointer items-center gap-2.5 text-sm text-navy-800">
      <input type="checkbox" checked={checked} onChange={onChange} className="h-4 w-4 rounded border-slate-300 text-iris-500 focus:ring-iris-400" />
      {label}
    </label>
  );
}

/* ------------------------------ Hotel card ------------------------------ */
function HotelCard({
  hotel,
  saved,
  onSave,
  onBook,
}: {
  hotel: Hotel;
  saved: boolean;
  onSave: () => void;
  onBook: () => void;
}) {
  const imgs = hotel.images.length ? hotel.images : hotel.image_url ? [hotel.image_url] : [];
  const [idx, setIdx] = useState(0);
  const has = imgs.length > 0;
  const go = (d: number) => setIdx((i) => (i + d + imgs.length) % imgs.length);

  return (
    <article className="group flex flex-col overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-card transition duration-300 hover:-translate-y-1 hover:shadow-lift">
      {/* Image carousel */}
      <div className="relative h-52 w-full overflow-hidden bg-slate-100">
        {has ? (
          <img src={imgs[idx]} alt={hotel.name} loading="lazy" className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" />
        ) : (
          <div className="grid h-full place-items-center text-sm text-slate-300">No image</div>
        )}

        {imgs.length > 1 && (
          <>
            <button
              onClick={() => go(-1)}
              aria-label="Previous image"
              className="absolute left-2 top-1/2 grid h-8 w-8 -translate-y-1/2 place-items-center rounded-full bg-white/85 text-navy-800 opacity-0 shadow transition group-hover:opacity-100 hover:bg-white"
            >
              <ChevronLeft size={16} />
            </button>
            <button
              onClick={() => go(1)}
              aria-label="Next image"
              className="absolute right-2 top-1/2 grid h-8 w-8 -translate-y-1/2 place-items-center rounded-full bg-white/85 text-navy-800 opacity-0 shadow transition group-hover:opacity-100 hover:bg-white"
            >
              <ChevronRight size={16} />
            </button>
            <div className="absolute bottom-2 left-1/2 flex -translate-x-1/2 gap-1.5">
              {imgs.map((_, i) => (
                <span key={i} className={`h-1.5 rounded-full transition-all ${i === idx ? "w-4 bg-white" : "w-1.5 bg-white/60"}`} />
              ))}
            </div>
          </>
        )}

        {hotel.property_type && (
          <span className="absolute left-3 top-3 rounded-full bg-navy-900/80 px-2.5 py-1 text-[11px] font-semibold text-gold-400 backdrop-blur">
            {hotel.property_type}
          </span>
        )}

        {/* Wishlist heart — visible to guests too; click routes through the auth gate */}
        <button
          onClick={onSave}
          aria-label={saved ? "Remove from wishlist" : "Save to wishlist"}
          className="absolute right-3 top-3 grid h-9 w-9 place-items-center rounded-full bg-white/85 shadow backdrop-blur transition hover:scale-110 hover:bg-white"
        >
          <Heart size={17} className={saved ? "fill-red-500 text-red-500" : "text-navy-700"} />
        </button>
      </div>

      {/* Body */}
      <div className="flex flex-1 flex-col p-4">
        <div className="flex items-start justify-between gap-2">
          <h3 className="font-display text-lg font-semibold text-navy-900">{hotel.name}</h3>
          <span className="flex shrink-0 items-center gap-1 rounded-md bg-gold-500/15 px-1.5 py-0.5 text-xs font-bold text-gold-600">
            <Star size={12} className="fill-gold-500 text-gold-500" />
            {hotel.rating.toFixed(1)}
          </span>
        </div>
        <p className="mt-1 flex items-center gap-1 text-sm text-slate-500">
          <MapPin size={14} className="text-iris-500" /> {hotel.location}
        </p>

        {hotel.amenities.length > 0 && (
          <div className="mt-3 flex flex-wrap gap-1.5">
            {hotel.amenities.slice(0, 3).map((a) => (
              <span key={a} className="rounded-md bg-cream px-2 py-0.5 text-[11px] text-navy-700">{a}</span>
            ))}
            {hotel.amenities.length > 3 && (
              <span className="rounded-md bg-cream px-2 py-0.5 text-[11px] text-slate-500">
                +{hotel.amenities.length - 3} more
              </span>
            )}
          </div>
        )}

        {/* Price + CTA pinned to the bottom */}
        <div className="mt-4 flex items-end justify-between gap-2 border-t border-slate-100 pt-4">
          <div>
            <div className="font-display text-2xl font-bold text-navy-900">
              ₹{hotel.price_per_night.toLocaleString("en-IN")}
            </div>
            <div className="text-xs text-slate-500">per night · incl. taxes</div>
          </div>
          <button onClick={onBook} className="btn-primary px-4 py-2 text-sm transition group-hover:shadow-iris">
            Book Now
          </button>
        </div>
      </div>
    </article>
  );
}